import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';
import {
  ArrowLeftCircle,
  BookOpenCheck,
  Brain,
  Database,
  Edit3,
  Layers3,
  LayoutGrid,
  Loader2,
  PlayCircle,
  PlusCircle,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud
} from 'lucide-react';
import FlashcardPractice from './FlashcardPractice';
import config from '../config';

const SQL_JS_CDN_URL = 'https://sql.js.org/dist/sql-wasm.js';
// Auto-detecta localhost / IP LAN / producción vía config.getApiUrl()
const getFlashcardApiBase = () =>
  process.env.REACT_APP_FLASHCARDS_API || `${config.getApiUrl()}/flashcards`;
const FLASHCARD_API_BASE = getFlashcardApiBase();
const DEFAULT_USER_ID = 'local-user';

const sanitizeFilename = (value = '') => {
  const cleaned = String(value)
    .replace(/[^a-z0-9_\- ]/gi, '')
    .trim();
  return cleaned || 'deck';
};

let sqlJsInitPromise = null;

const loadSqlJsInitializer = () => {
  if (sqlJsInitPromise) return sqlJsInitPromise;
  if (typeof window === 'undefined') {
    sqlJsInitPromise = Promise.reject(new Error('sql.js solo se soporta en navegadores.'));
    return sqlJsInitPromise;
  }
  if (window.initSqlJs) {
    sqlJsInitPromise = Promise.resolve(window.initSqlJs);
    return sqlJsInitPromise;
  }

  sqlJsInitPromise = new Promise((resolve, reject) => {
    const finalize = () => {
      if (window.initSqlJs) resolve(window.initSqlJs);
      else reject(new Error('sql.js no se inicializó correctamente.'));
    };
    const handleError = () => reject(new Error('No se pudo cargar sql.js desde la red.'));
    const existing = document.querySelector('script[data-sqljs]');
    if (existing) {
      existing.addEventListener('load', finalize, { once: true });
      existing.addEventListener('error', handleError, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.dataset.sqljs = 'true';
    script.src = SQL_JS_CDN_URL;
    script.onload = finalize;
    script.onerror = handleError;
    document.body.appendChild(script);
  });

  return sqlJsInitPromise;
};

const SAMPLE_DECK = {
  deck: {
    name: 'Neonatología · Sepsis',
    area: 'Neonatología',
    topic: 'Sepsis neonatal',
    subtopic: 'Enfoque inicial',
    intent: 'Repaso rápido post simulacro',
    description: 'Conceptos de detección temprana, antibióticos empíricos y soporte hemodinámico.'
  },
  cards: [
    {
      front: 'Signos cardinales de sepsis neonatal tardía',
      back: 'Inestabilidad térmica, mala succión, apnea/taquipnea, letargo o irritabilidad y perfusión deficiente.',
      mnemonic: 'TALPI (Temperatura, Alimentación, Latencia respiratoria, Perfusión, Irritabilidad)',
      references: 'Guía Neonatal MINSA 2023',
      tags: ['sepsis', 'signos']
    },
    {
      front: 'Antibióticos empíricos recomendados para sepsis neonatal temprana',
      back: 'Ampicilina + Gentamicina cubriendo Streptococcus B y bacilos gramnegativos; ajustar según cultivo.',
      mnemonic: 'AG temprana',
      references: 'RMN Perú 2024',
      tags: ['antibioticos']
    },
    {
      front: 'Diferencias entre sepsis temprana y tardía',
      back: 'Temprana (<72h) asociada a transmisión vertical. Tardía (>72h) relacionada al ambiente hospitalario y catéteres.',
      mnemonic: 'VT-Hosp (Vertical vs Hospitalario)',
      references: 'UpToDate Sepsis Neonatal 2024',
      tags: ['comparativo']
    }
  ]
};

const FlashcardStudio = ({ darkMode, onBack, user }) => {
  const [decks, setDecks] = useState([]);
  const [selectedDeckId, setSelectedDeckId] = useState(null);
  const [cardsByDeck, setCardsByDeck] = useState({});
  const [loadingDecks, setLoadingDecks] = useState(true);
  const [loadingCards, setLoadingCards] = useState(false);
  const [apiError, setApiError] = useState(null);
  const isDevelopment = process.env.NODE_ENV === 'development';
  const effectiveUserId = user?.id || (isDevelopment ? DEFAULT_USER_ID : null);

  const [newDeck, setNewDeck] = useState({
    name: '',
    area: '',
    topic: '',
    subtopic: '',
    intent: 'Repaso intensivo post simulacro',
    description: ''
  });

  const [cardForm, setCardForm] = useState({
    front: '',
    back: '',
    area: '',
    topic: '',
    subtopic: '',
    mnemonic: '',
    references: '',
    tags: ''
  });
  const [editingCardId, setEditingCardId] = useState(null);
  const [isSavingCard, setIsSavingCard] = useState(false);

  const [importContext, setImportContext] = useState({ area: '', topic: '', subtopic: '' });
  const [isImporting, setIsImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);
  const sqlModuleRef = useRef(null);
  const [practiceDeck, setPracticeDeck] = useState(null);
  const [practiceCards, setPracticeCards] = useState([]);
  const [loadingPractice, setLoadingPractice] = useState(false);
  const [deckModal, setDeckModal] = useState({ open: false, data: null, saving: false });
  const [selectedCardIds, setSelectedCardIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const inputClasses = `rounded-lg px-3 py-2 border ${
    darkMode
      ? 'border-slate-700 bg-slate-900/40 text-white placeholder:text-slate-400'
      : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-500'
  }`;

  const textareaClasses = `rounded-xl border ${
    darkMode
      ? 'border-slate-700 bg-slate-900/30 text-white placeholder:text-slate-400'
      : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-500'
  } px-4 py-3`;

  const panelStyles = darkMode
    ? 'bg-slate-900/70 border border-slate-700 text-slate-100'
    : 'bg-white/95 border border-slate-200 text-slate-800';

  const glassStyles = darkMode
    ? 'bg-white/5 border border-white/10'
    : 'bg-white shadow-lg border border-white/40';

  const fetchJson = useCallback(async (path, options = {}) => {
    const response = await fetch(`${FLASHCARD_API_BASE}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      ...options
    });

    if (!response.ok) {
      let message = `Error ${response.status}`;
      try {
        const payload = await response.json();
        message = payload.error || payload.message || message;
      } catch (_) {
        /* ignore */
      }
      throw new Error(message);
    }

    if (response.status === 204) return null;
    return response.json();
  }, []);

  const fetchDecks = useCallback(async () => {
    if (!effectiveUserId) {
      setApiError('Inicia sesión para usar Flashcard Studio');
      setLoadingDecks(false);
      return;
    }
    setLoadingDecks(true);
    setApiError(null);
    try {
      const payload = await fetchJson(`/decks?withCounts=true&userId=${encodeURIComponent(effectiveUserId)}`);
      setDecks(payload.decks || []);
      if (!selectedDeckId && (payload.decks || []).length) {
        setSelectedDeckId(payload.decks[0].id);
      }
    } catch (error) {
      setApiError(error.message);
    } finally {
      setLoadingDecks(false);
    }
  }, [fetchJson, selectedDeckId, effectiveUserId]);

  const fetchDeckCards = useCallback(
    async (deckId) => {
      if (!deckId) return;
      if (!effectiveUserId) {
        setApiError('Inicia sesión para usar Flashcard Studio');
        return;
      }
      setLoadingCards(true);
      setApiError(null);
      try {
        const payload = await fetchJson(`/decks/${deckId}/cards?userId=${encodeURIComponent(effectiveUserId)}`);
        setCardsByDeck((prev) => ({ ...prev, [deckId]: payload.cards || [] }));
      } catch (error) {
        setApiError(error.message);
      } finally {
        setLoadingCards(false);
        setSelectedCardIds(new Set());
      }
    },
    [fetchJson, effectiveUserId]
  );

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  useEffect(() => {
    if (selectedDeckId && !cardsByDeck[selectedDeckId]) {
      fetchDeckCards(selectedDeckId);
    }
  }, [selectedDeckId, cardsByDeck, fetchDeckCards]);

  const currentDeck = useMemo(() => {
    if (!decks.length) return null;
    return decks.find((deck) => deck.id === selectedDeckId) || decks[0];
  }, [decks, selectedDeckId]);

  const filteredCards = useMemo(() => {
    if (!currentDeck) return [];
    return cardsByDeck[currentDeck.id] || [];
  }, [cardsByDeck, currentDeck]);

  const focusInsights = useMemo(() => {
    const areas = {};
    filteredCards.forEach((card) => {
      if (!card.area) return;
      areas[card.area] = (areas[card.area] || 0) + 1;
    });
    return {
      totalDecks: decks.length,
      totalCards: decks.reduce((sum, deck) => sum + (deck.card_count || 0), 0),
      distinctAreas: Object.keys(areas).length
    };
  }, [decks, filteredCards]);

  const normalizeTags = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    return String(tags)
      .split(/[, ]+/)
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  const handleCreateDeck = async () => {
    if (!newDeck.name.trim()) {
      setImportSummary({ type: 'error', message: 'Asigna un nombre a la colección.' });
      return;
    }
    try {
      if (!effectiveUserId) {
        setImportSummary({ type: 'error', message: 'Inicia sesión para crear una colección.' });
        return;
      }
      const payload = await fetchJson('/decks', {
        method: 'POST',
        body: JSON.stringify({
          ...newDeck,
          userId: effectiveUserId,
          tags: normalizeTags(newDeck.tags)
        })
      });
      setDecks((prev) => [payload.deck, ...prev]);
      setSelectedDeckId(payload.deck.id);
      setNewDeck({
        name: '',
        area: '',
        topic: '',
        subtopic: '',
        intent: 'Repaso intensivo post simulacro',
        description: ''
      });
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    }
  };

  const resetCardForm = () => {
    setCardForm({
      front: '',
      back: '',
      area: '',
      topic: '',
      subtopic: '',
      mnemonic: '',
      references: '',
      tags: ''
    });
    setEditingCardId(null);
  };

  const handleSaveCard = async () => {
    if (!currentDeck) {
      setImportSummary({ type: 'error', message: 'Selecciona una colección primero.' });
      return;
    }
    if (!effectiveUserId) {
      setImportSummary({ type: 'error', message: 'Inicia sesión para guardar tus flashcards.' });
      return;
    }
    if (!cardForm.front.trim() || !cardForm.back.trim()) {
      setImportSummary({ type: 'error', message: 'Frente y reverso son obligatorios.' });
      return;
    }

    try {
      if (editingCardId) {
        const payload = await fetchJson(`/cards/${editingCardId}`, {
          method: 'PUT',
          body: JSON.stringify({
            front: cardForm.front,
            back: cardForm.back,
            mnemonic: cardForm.mnemonic,
            references: cardForm.references,
            area: cardForm.area,
            topic: cardForm.topic,
            subtopic: cardForm.subtopic,
            tags: normalizeTags(cardForm.tags),
            difficulty: 'core',
            userId: effectiveUserId
          })
        });
        setCardsByDeck((prev) => ({
          ...prev,
          [currentDeck.id]: (prev[currentDeck.id] || []).map((card) =>
            card.id === editingCardId ? payload.card : card
          )
        }));
        setImportSummary({ type: 'success', message: 'Tarjeta actualizada.' });
      } else {
        const payload = await fetchJson('/cards', {
          method: 'POST',
          body: JSON.stringify({
            ...cardForm,
            deckId: currentDeck.id,
            tags: normalizeTags(cardForm.tags),
            area: cardForm.area || currentDeck.area || importContext.area,
            topic: cardForm.topic || currentDeck.topic || importContext.topic,
            subtopic: cardForm.subtopic || currentDeck.subtopic || importContext.subtopic,
            userId: effectiveUserId
          })
        });

        setCardsByDeck((prev) => ({
          ...prev,
          [currentDeck.id]: [payload.card, ...(prev[currentDeck.id] || [])]
        }));
        setImportSummary({ type: 'success', message: 'Tarjeta creada.' });
      }

      resetCardForm();
      fetchDecks();
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    }
  };

  const beginEditCard = (card) => {
    setCardForm({
      front: card.front || '',
      back: card.back || '',
      area: card.area || '',
      topic: card.topic || '',
      subtopic: card.subtopic || '',
      mnemonic: card.mnemonic || '',
      references: card.references || '',
      tags: (card.tags || []).join(', ')
    });
    setEditingCardId(card.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteCard = async (cardId) => {
    const confirmed = window.confirm('¿Eliminar esta flashcard? No podrás deshacer esta acción.');
    if (!confirmed) return;
    await handleBulkDelete(new Set([cardId]));
  };

  const handleBulkDelete = async (idsSet = selectedCardIds) => {
    if (!idsSet.size) return;
    if (!effectiveUserId) {
      setImportSummary({ type: 'error', message: 'Inicia sesión para administrar tus flashcards.' });
      return;
    }
    const cardsArray = Array.from(idsSet);
    const confirmation = window.confirm(`Eliminar ${cardsArray.length} tarjetas seleccionadas?`);
    if (!confirmation) return;
    setBulkDeleting(true);
    try {
      for (const cardId of cardsArray) {
        await fetchJson(`/cards/${cardId}?userId=${encodeURIComponent(effectiveUserId)}`, { method: 'DELETE' });
      }
      setCardsByDeck((prev) => ({
        ...prev,
        [currentDeck.id]: (prev[currentDeck.id] || []).filter((card) => !idsSet.has(card.id))
      }));
      if (idsSet.has(editingCardId)) {
        resetCardForm();
      }
      setSelectedCardIds(new Set());
      setImportSummary({ type: 'success', message: 'Tarjetas eliminadas.' });
      fetchDecks();
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    } finally {
      setBulkDeleting(false);
    }
  };

  const ensureSqlModule = async () => {
    if (sqlModuleRef.current) return sqlModuleRef.current;
    const initSqlJs = await loadSqlJsInitializer();
    const SQL = await initSqlJs({
      locateFile: (file) => `https://sql.js.org/dist/${file}`
    });
    sqlModuleRef.current = SQL;
    return SQL;
  };

  const inferValueFromTags = (tagList, prefix) => {
    if (!tagList?.length) return '';
    const match = tagList.find((tag) => tag.toLowerCase().startsWith(prefix));
    if (!match) return '';
    return match.split('::')[1] || '';
  };

  const importApkg = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);
    const collectionFile =
      zip.file('collection.anki21') ||
      zip.file('collection.anki2') ||
      zip.file('collection.anki');
    if (!collectionFile) {
      throw new Error('El archivo .apkg no contiene collection.anki2');
    }
    const collectionBuffer = await collectionFile.async('uint8array');
    const SQL = await ensureSqlModule();
    const db = new SQL.Database(collectionBuffer);

    const deckRows = db.exec('SELECT decks FROM col LIMIT 1');
    const deckMap = deckRows.length ? JSON.parse(deckRows[0].values[0][0]) : {};

    const notesRows = db.exec(`
      SELECT n.id as noteId, n.flds as fields, n.tags as tags, c.did as deckId
      FROM notes n
      JOIN cards c ON c.nid = n.id
    `);
    if (!notesRows.length) return [];
    const { values } = notesRows[0];
    const seen = new Set();

    return values
      .map((row) => {
        const [noteId, rawFields, rawTags, deckId] = row;
        if (seen.has(noteId)) return null;
        seen.add(noteId);
        const [front = '', back = '', extra = ''] = (rawFields || '').split('\u001f');
        const tags = (rawTags || '')
          .trim()
          .split(' ')
          .map((tag) => tag.trim())
          .filter(Boolean);
        const area = inferValueFromTags(tags, 'area::') || importContext.area;
        const topic =
          inferValueFromTags(tags, 'tema::') ||
          inferValueFromTags(tags, 'topic::') ||
          importContext.topic;
        const subtopic =
          inferValueFromTags(tags, 'subtema::') ||
          inferValueFromTags(tags, 'subtopic::') ||
          importContext.subtopic;

        return {
          front: front || extra,
          back: back || extra,
          tags,
          sourceDeckName: deckMap[String(deckId)]?.name || file.name.replace('.apkg', ''),
          area,
          topic,
          subtopic
        };
      })
      .filter(Boolean);
  };

const importDelimitedFile = async (file, delimiter = ',') => {
  const text = await file.text();

  const parseWithHeader = () =>
    new Promise((resolve, reject) => {
      Papa.parse(text, {
        header: true,
        delimiter,
        skipEmptyLines: 'greedy',
        complete: (result) => {
          if (result.errors?.length) {
            reject(new Error(result.errors[0].message));
            return;
          }
          resolve(result);
        },
        error: (error) => reject(error)
      });
    });

  const parseWithoutHeader = () =>
    new Promise((resolve, reject) => {
      Papa.parse(text, {
        header: false,
        delimiter,
        skipEmptyLines: 'greedy',
        complete: (result) => {
          if (result.errors?.length) {
            reject(new Error(result.errors[0].message));
            return;
          }
          resolve(result);
        },
        error: (error) => reject(error)
      });
    });

  const mapRow = (row = {}) => ({
    front: row.Front || row.front || row.Question || '',
    back: row.Back || row.back || row.Answer || '',
    mnemonic: row.Mnemonic || row.Helper || '',
    references: row.Source || row.Reference || '',
    area: row.Area || importContext.area,
    topic: row.Topic || importContext.topic,
    subtopic: row.Subtopic || importContext.subtopic,
    tags: normalizeTags(row.Tags || row.Tag)
  });

  const headerResult = await parseWithHeader();
  let dataset = headerResult.data.map(mapRow);

  const headerLooksSuspicious =
    !(headerResult.meta?.fields || []).some((field = '') =>
      /(front|question|pregunta|back|respuesta)/i.test(field)
    );
  const hasNoFrontValues = dataset.every((row) => !(row.front || '').trim());

  if (headerLooksSuspicious || hasNoFrontValues) {
    const fallback = await parseWithoutHeader();
    dataset = fallback.data.map((columns = []) => {
      const [front = '', back = '', ...rest] = columns;
      return {
        front,
        back,
        mnemonic: '',
        references: '',
        area: importContext.area,
        topic: importContext.topic,
        subtopic: importContext.subtopic,
        tags: normalizeTags(rest.join(' '))
      };
    });
  }

  return dataset.filter((row) => row.front || row.back);
};

  const persistImportedCards = async (deckBlueprint, cards) => {
    if (!cards.length) {
      setImportSummary({ type: 'error', message: 'No se encontraron tarjetas válidas.' });
      return;
    }
    if (!effectiveUserId) {
      setImportSummary({ type: 'error', message: 'Inicia sesión para importar tus flashcards.' });
      return;
    }
    try {
      const payload = await fetchJson('/import', {
        method: 'POST',
        body: JSON.stringify({
          deck: {
            ...deckBlueprint,
            userId: effectiveUserId
          },
          cards
        })
      });
      setImportSummary({
        type: 'success',
        message: `Se importaron ${payload.insertedCards} tarjetas en “${payload.deck.name}”.`
      });
      fetchDecks();
      setCardsByDeck((prev) => ({
        ...prev,
        [payload.deck.id]: payload.cards || []
      }));
      setSelectedDeckId(payload.deck.id);
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    } finally {
      setIsImporting(false);
    }
  };

  const startPracticeSession = async (deck) => {
    if (!effectiveUserId) {
      setImportSummary({ type: 'error', message: 'Inicia sesión para practicar tus decks.' });
      return;
    }
    setLoadingPractice(true);
    setImportSummary(null);
    try {
      if (!cardsByDeck[deck.id]) {
        const payload = await fetchJson(`/decks/${deck.id}/cards?userId=${encodeURIComponent(effectiveUserId)}`);
        setCardsByDeck((prev) => ({ ...prev, [deck.id]: payload.cards || [] }));
        if (!payload.cards?.length) {
          setImportSummary({ type: 'error', message: 'Este deck aún no tiene tarjetas.' });
          return;
        }
        setPracticeCards(payload.cards);
      } else {
        if (!(cardsByDeck[deck.id] || []).length) {
          setImportSummary({ type: 'error', message: 'Este deck aún no tiene tarjetas.' });
          return;
        }
        setPracticeCards(cardsByDeck[deck.id]);
      }
      setPracticeDeck(deck);
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    } finally {
      setLoadingPractice(false);
    }
  };

  const openDeckEditor = (deck) => {
    setDeckModal({
      open: true,
      saving: false,
      data: {
        id: deck.id,
        name: deck.name || '',
        area: deck.area || '',
        topic: deck.topic || '',
        subtopic: deck.subtopic || '',
        intent: deck.intent || '',
        description: deck.description || '',
        visibility: deck.visibility || 'private',
        tagsInput: Array.isArray(deck.tags) ? deck.tags.join(', ') : ''
      }
    });
  };

  const closeDeckEditor = () => setDeckModal({ open: false, data: null, saving: false });

  const updateDeckModalField = (field, value) => {
    setDeckModal((prev) => ({
      ...prev,
      data: { ...prev.data, [field]: value }
    }));
  };

  const handleDeckUpdate = async () => {
    if (!deckModal.data || !effectiveUserId) return;
    setDeckModal((prev) => ({ ...prev, saving: true }));
    try {
      const payload = await fetchJson(`/decks/${deckModal.data.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: deckModal.data.name,
          area: deckModal.data.area,
          topic: deckModal.data.topic,
          subtopic: deckModal.data.subtopic,
          intent: deckModal.data.intent,
          description: deckModal.data.description,
          visibility: deckModal.data.visibility,
          tags: normalizeTags(deckModal.data.tagsInput),
          userId: effectiveUserId
        })
      });
      setDecks((prev) =>
        prev.map((deck) => (deck.id === payload.deck.id ? payload.deck : deck))
      );
      if (currentDeck?.id === payload.deck.id) {
        setSelectedDeckId(payload.deck.id);
      }
      closeDeckEditor();
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
      setDeckModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeckDelete = async (deck) => {
    if (!effectiveUserId) {
      setImportSummary({ type: 'error', message: 'Inicia sesión para administrar tus decks.' });
      return;
    }
    const confirmation = window.confirm(
      `¿Eliminar la colección “${deck.name}” y todas sus tarjetas?`
    );
    if (!confirmation) return;
    try {
      await fetchJson(`/decks/${deck.id}?userId=${encodeURIComponent(effectiveUserId)}`, {
        method: 'DELETE'
      });
      setDecks((prev) => prev.filter((d) => d.id !== deck.id));
      setCardsByDeck((prev) => {
        const clone = { ...prev };
        delete clone[deck.id];
        return clone;
      });
      if (selectedDeckId === deck.id) {
        setSelectedDeckId(null);
      }
      if (practiceDeck?.id === deck.id) {
        setPracticeDeck(null);
      }
      setImportSummary({ type: 'success', message: 'Deck eliminado correctamente.' });
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    }
  };

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    const file = fileList[0];
    setIsImporting(true);
    try {
      const lower = file.name.toLowerCase();
      let imported;
      if (lower.endsWith('.apkg')) {
        imported = await importApkg(file);
      } else {
        const delimiter = lower.endsWith('.tsv') || lower.endsWith('.txt') ? '\t' : ',';
        imported = await importDelimitedFile(file, delimiter);
      }
      if (!imported.length) {
        setImportSummary({ type: 'error', message: 'No se pudieron leer tarjetas.' });
        return;
      }
      const deckName =
        imported[0].sourceDeckName || currentDeck?.name || file.name.replace(/\.\w+$/, '');
      await persistImportedCards(
        {
          id: currentDeck?.id,
          name: deckName,
          area: importContext.area || currentDeck?.area,
          topic: importContext.topic || currentDeck?.topic,
          subtopic: importContext.subtopic || currentDeck?.subtopic
        },
        imported.map((card) => ({
          ...card,
          tags: normalizeTags(card.tags),
          area: card.area || importContext.area,
          topic: card.topic || importContext.topic,
          subtopic: card.subtopic || importContext.subtopic
        }))
      );
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const exportDeck = (format = 'csv') => {
    if (!currentDeck || !filteredCards.length) {
      setImportSummary({ type: 'error', message: 'Selecciona un deck con tarjetas para exportar.' });
      return;
    }
    const dataset = filteredCards.map((card) => ({
      Front: card.front,
      Back: card.back,
      Area: card.area,
      Topic: card.topic,
      Subtopic: card.subtopic,
      Mnemonic: card.mnemonic,
      Source: card.references,
      Tags: (card.tags || []).join(' ')
    }));
    const delimiter = format === 'tsv' ? '\t' : ',';
    const mime =
      format === 'tsv' ? 'text/tab-separated-values;charset=utf-8;' : 'text/csv;charset=utf-8;';
    const serialized = Papa.unparse(dataset, { quotes: true, delimiter });
    const blob = new Blob([serialized], { type: mime });
    saveAs(blob, `${currentDeck.name || 'deck'}-flashcards.${format}`);
  };

  const exportDeckApkg = async () => {
    if (!currentDeck) {
      setImportSummary({ type: 'error', message: 'Selecciona un deck para exportar.' });
      return;
    }
    if (!effectiveUserId) {
      setImportSummary({ type: 'error', message: 'Inicia sesión para exportar tus decks.' });
      return;
    }
    setImportSummary(null);
    try {
      const endpoint = `${FLASHCARD_API_BASE}/decks/${currentDeck.id}/export-apkg?userId=${encodeURIComponent(
        effectiveUserId
      )}`;
      const response = await fetch(endpoint);
      if (!response.ok) {
        let message = `Error ${response.status}`;
        try {
          const payload = await response.json();
          message = payload.error || payload.details || message;
        } catch (_) {
          /* ignore */
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      saveAs(blob, `${sanitizeFilename(currentDeck.name || 'deck')}.apkg`);
      setImportSummary({ type: 'success', message: 'Deck exportado en formato .apkg' });
    } catch (error) {
      setImportSummary({ type: 'error', message: error.message });
    }
  };

  const handleTemplateInjection = () =>
    persistImportedCards(SAMPLE_DECK.deck, SAMPLE_DECK.cards.map((card) => ({
      ...card,
      tags: normalizeTags(card.tags)
    })));

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files?.length) {
      handleFiles(event.dataTransfer.files);
    }
  };

  const StatCard = ({ icon, label, value }) => (
    <div
      className={`rounded-2xl p-5 flex flex-col gap-2 ${
        darkMode ? 'bg-slate-900/80 border border-slate-700' : 'bg-white border border-slate-200'
      }`}
    >
      <div className="text-sm uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-3xl font-bold">{value ?? '—'}</div>
      <div className="text-cyan-400">{icon}</div>
    </div>
  );

  return (
    <>
    <section className="space-y-8">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200/50 dark:bg-slate-800 hover:bg-slate-200/80 dark:hover:bg-slate-700 transition"
        >
          <ArrowLeftCircle className="w-5 h-5" />
          Volver
        </button>
        <span
          className={`px-4 py-2 rounded-full text-sm font-semibold ${
            darkMode
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
              : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
          }`}
        >
          Persistencia en MySQL (local) + Supabase (cloud)
        </span>
      </div>

      <div className={`relative overflow-hidden rounded-3xl ${glassStyles} p-10`}>
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-fuchsia-500/10 to-emerald-500/10 pointer-events-none" />
        <div className="relative z-10 text-center space-y-4">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/20 backdrop-blur">
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="font-semibold">Flashcard Studio · MySQL / Supabase</span>
          </div>
          <h1 className="text-4xl font-bold">Convierte tus errores en decks clínicos profesionales</h1>
          <p className="text-lg text-white/80 max-w-3xl mx-auto">
            Importa archivos CSV o Anki (.apkg), edita tarjetas y sincroniza en caliente con tu base
            local y la nube. Perfecto para repasar Neonatología, Sepsis u otras áreas post simulacro.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white">
              <Brain className="w-4 h-4" /> Preparado para algoritmos SM/FSRS
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white">
              <UploadCloud className="w-4 h-4" /> Importa CSV / .apkg
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white">
              <ShieldCheck className="w-4 h-4" /> Fuente única MySQL ↔ Supabase
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
        <StatCard icon={<Layers3 className="w-5 h-5" />} label="Decks activos" value={focusInsights.totalDecks} />
        <StatCard icon={<LayoutGrid className="w-5 h-5" />} label="Flashcards" value={focusInsights.totalCards} />
        <StatCard icon={<BookOpenCheck className="w-5 h-5" />} label="Áreas cubiertas" value={focusInsights.distinctAreas} />
        <StatCard icon={<Sparkles className="w-5 h-5" />} label="Deck seleccionado" value={currentDeck?.name || '—'} />
      </div>

      {apiError && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-rose-200">
          {apiError}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <div className={`rounded-2xl p-5 ${panelStyles}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm uppercase tracking-wide opacity-70">Colecciones</p>
                <h3 className="text-xl font-semibold">Panel de decks</h3>
              </div>
              <button
                onClick={handleTemplateInjection}
                className="text-sm font-semibold px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20"
              >
                Plantilla Neonatología
              </button>
            </div>
            {loadingDecks ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
              </div>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {decks.map((deck) => (
                  <div
                    key={deck.id}
                    className={`w-full p-4 rounded-xl border transition ${
                      deck.id === currentDeck?.id
                        ? 'border-cyan-400 bg-cyan-400/10'
                        : 'border-slate-600/40 hover:border-cyan-400/50'
                    }`}
                  >
                    <button
                      onClick={() => setSelectedDeckId(deck.id)}
                      className="text-left w-full"
                    >
                      <p className="text-sm uppercase tracking-wide opacity-70">
                        {deck.area || 'Sin área'}
                      </p>
                      <p className="text-lg font-semibold">{deck.name}</p>
                      <p className="text-xs opacity-70 flex items-center justify-between">
                        <span>{deck.intent || 'Personalizado'}</span>
                        <span>{deck.card_count || 0} tarjetas</span>
                      </p>
                    </button>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => startPracticeSession(deck)}
                        className="flex items-center justify-center gap-2 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold hover:shadow-lg"
                      >
                        <PlayCircle className="w-4 h-4" />
                        Practicar
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDeckId(deck.id);
                          fetchDeckCards(deck.id);
                        }}
                        className="px-3 py-2 text-xs rounded-lg border border-slate-600 hover:border-cyan-400"
                      >
                        Refrescar
                      </button>
                      <button
                        onClick={() => openDeckEditor(deck)}
                        className="flex items-center justify-center gap-2 py-2 rounded-lg border border-purple-400 text-purple-200 text-xs hover:bg-purple-500/10"
                      >
                        <Edit3 className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeckDelete(deck)}
                        className="flex items-center justify-center gap-2 py-2 rounded-lg border border-rose-500 text-rose-200 text-xs hover:bg-rose-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
                {!decks.length && <p className="text-sm opacity-70">Aún no tienes decks guardados.</p>}
              </div>
            )}
          </div>

          <div className={`rounded-2xl p-5 space-y-4 ${panelStyles}`}>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-cyan-400" /> Nueva colección
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nombre del deck"
                value={newDeck.name}
                onChange={(e) => setNewDeck((prev) => ({ ...prev, name: e.target.value }))}
                className={`${inputClasses} w-full`}
              />
              <input
                type="text"
                placeholder="Área (ej. Neonatología)"
                value={newDeck.area}
                onChange={(e) => setNewDeck((prev) => ({ ...prev, area: e.target.value }))}
                className={`${inputClasses} w-full`}
              />
              <input
                type="text"
                placeholder="Tema principal"
                value={newDeck.topic}
                onChange={(e) => setNewDeck((prev) => ({ ...prev, topic: e.target.value }))}
                className={`${inputClasses} w-full`}
              />
              <textarea
                rows={2}
                placeholder="Objetivo del deck"
                value={newDeck.intent}
                onChange={(e) => setNewDeck((prev) => ({ ...prev, intent: e.target.value }))}
                className={`${textareaClasses} w-full`}
              />
              <button
                onClick={handleCreateDeck}
                className="w-full py-2 rounded-lg bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition"
              >
                Guardar colección
              </button>
            </div>
          </div>

          <div className={`rounded-2xl p-5 space-y-4 ${panelStyles}`}>
            <div className="flex items-center gap-2">
              <UploadCloud className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold">Importar flashcards</h3>
            </div>
            <p className="text-sm opacity-70">
              Drag & drop de archivos .apkg, .csv o .txt exportados desde Anki. Añade un contexto para
              etiquetar área/tema automáticamente.
            </p>
            <div className="grid gap-2">
              <input
                type="text"
                placeholder="Área por defecto"
                value={importContext.area}
                onChange={(e) => setImportContext((prev) => ({ ...prev, area: e.target.value }))}
                className={`${inputClasses} w-full`}
              />
              <input
                type="text"
                placeholder="Tema por defecto"
                value={importContext.topic}
                onChange={(e) => setImportContext((prev) => ({ ...prev, topic: e.target.value }))}
                className={`${inputClasses} w-full`}
              />
              <input
                type="text"
                placeholder="Subtema por defecto"
                value={importContext.subtopic}
                onChange={(e) => setImportContext((prev) => ({ ...prev, subtopic: e.target.value }))}
                className={`${inputClasses} w-full`}
              />
            </div>
            <div
              className="rounded-xl border-2 border-dashed border-slate-600/60 px-4 py-8 text-center cursor-pointer hover:border-cyan-400/70 transition"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
            >
              {isImporting ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                  <p className="text-sm">Procesando archivo...</p>
                </div>
              ) : (
                <>
                  <p className="font-semibold">Arrastra tu archivo o haz clic aquí</p>
                  <p className="text-xs opacity-70">Compatible con Anki (.apkg) y CSV (Front,Back,Tags,...)</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt,.apkg"
                className="hidden"
                onChange={(event) => handleFiles(event.target.files)}
              />
            </div>
            {importSummary && (
              <div
                className={`text-sm rounded-lg px-3 py-2 ${
                  importSummary.type === 'success'
                    ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                    : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
                }`}
              >
                {importSummary.message}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <div className={`rounded-2xl p-6 ${panelStyles}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm uppercase tracking-wide opacity-70">Editor</p>
                <h3 className="text-xl font-semibold">
                  {currentDeck ? currentDeck.name : 'Selecciona una colección'}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => exportDeck('csv')}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-purple-500/20 text-purple-200 border border-purple-500/40 hover:bg-purple-500/30"
                >
                  Exportar CSV
                </button>
                <button
                  onClick={() => exportDeck('tsv')}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-blue-500/20 text-blue-200 border border-blue-500/40 hover:bg-blue-500/30"
                >
                  Exportar TSV
                </button>
                <button
                  onClick={exportDeckApkg}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/30"
                >
                  Exportar Anki (.apkg)
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <textarea
                rows={4}
                placeholder="Pregunta / disparador clínico"
                value={cardForm.front}
                onChange={(e) => setCardForm((prev) => ({ ...prev, front: e.target.value }))}
                className={`${textareaClasses} w-full`}
              />
              <textarea
                rows={4}
                placeholder="Respuesta con algoritmo, perlas o tabla resumida"
                value={cardForm.back}
                onChange={(e) => setCardForm((prev) => ({ ...prev, back: e.target.value }))}
                className={`${textareaClasses} w-full`}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-3 mt-4">
              <input
                type="text"
                placeholder="Área"
                value={cardForm.area}
                onChange={(e) => setCardForm((prev) => ({ ...prev, area: e.target.value }))}
                className={`${inputClasses} rounded-xl`}
              />
              <input
                type="text"
                placeholder="Tema"
                value={cardForm.topic}
                onChange={(e) => setCardForm((prev) => ({ ...prev, topic: e.target.value }))}
                className={`${inputClasses} rounded-xl`}
              />
              <input
                type="text"
                placeholder="Subtema"
                value={cardForm.subtopic}
                onChange={(e) => setCardForm((prev) => ({ ...prev, subtopic: e.target.value }))}
                className={`${inputClasses} rounded-xl`}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-3 mt-4">
              <input
                type="text"
                placeholder="Mnemotecnia"
                value={cardForm.mnemonic}
                onChange={(e) => setCardForm((prev) => ({ ...prev, mnemonic: e.target.value }))}
                className={`${inputClasses} rounded-xl`}
              />
              <input
                type="text"
                placeholder="Fuente / video"
                value={cardForm.references}
                onChange={(e) => setCardForm((prev) => ({ ...prev, references: e.target.value }))}
                className={`${inputClasses} rounded-xl`}
              />
              <input
                type="text"
                placeholder="Tags (coma separada)"
                value={cardForm.tags}
                onChange={(e) => setCardForm((prev) => ({ ...prev, tags: e.target.value }))}
                className={`${inputClasses} rounded-xl`}
              />
            </div>
            {editingCardId && (
              <div className="mt-4 text-sm text-amber-400">
                Editando una tarjeta existente. Guarda los cambios o cancela para crear una nueva.
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={handleSaveCard}
                disabled={isSavingCard}
                className={`px-6 py-3 rounded-full text-white font-semibold transition ${
                  isSavingCard
                    ? 'bg-emerald-400 cursor-not-allowed'
                    : 'bg-emerald-500 hover:bg-emerald-600'
                }`}
              >
                {editingCardId ? 'Actualizar flashcard' : 'Guardar flashcard'}
              </button>
              <button
                type="button"
                onClick={resetCardForm}
                className="px-6 py-3 rounded-full border border-slate-600 hover:bg-slate-700 transition"
              >
                {editingCardId ? 'Cancelar edición' : 'Limpiar campos'}
              </button>
            </div>
          </div>

          <div className={`rounded-2xl p-6 ${panelStyles}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold">Flashcards destacadas</h3>
                {filteredCards.length > 0 && (
                  <label className="flex items-center gap-2 text-sm opacity-80 mt-1">
                    <input
                      type="checkbox"
                      checked={selectedCardIds.size > 0 && selectedCardIds.size === filteredCards.length}
                      indeterminate={selectedCardIds.size > 0 && selectedCardIds.size < filteredCards.length ? 'true' : undefined}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCardIds(new Set(filteredCards.map((card) => card.id)));
                        } else {
                          setSelectedCardIds(new Set());
                        }
                      }}
                      className="accent-cyan-500"
                    />
                    Seleccionar todas ({selectedCardIds.size})
                  </label>
                )}
              </div>
              {loadingCards && (
                <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
              )}
              <Database className="w-5 h-5 opacity-70" />
            </div>
            {filteredCards.length === 0 ? (
              <div className="text-center py-10 opacity-70">
                {currentDeck
                  ? 'Aún no hay tarjetas en este deck. Crea o importa para comenzar.'
                  : 'Selecciona un deck para ver sus tarjetas.'}
              </div>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
                {filteredCards.map((card) => (
                  <article
                    key={card.id}
                    className={`rounded-2xl border p-4 bg-slate-900/40 transition ${
                      selectedCardIds.has(card.id)
                        ? 'border-emerald-400 bg-emerald-500/10'
                        : 'border-slate-700/60 hover:border-cyan-400/60'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={selectedCardIds.has(card.id)}
                          onChange={(e) => {
                            setSelectedCardIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                next.add(card.id);
                              } else {
                                next.delete(card.id);
                              }
                              return next;
                            });
                            if (e.target.checked && practiceDeck?.id === currentDeck?.id) {
                              setPracticeDeck(null);
                            }
                          }}
                          className="accent-cyan-500"
                        />
                        Seleccionar
                      </label>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-slate-800/70">
                        {(card.tags && card.tags[0]) || 'sin-tag'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wide opacity-60">
                        {card.area || 'Área general'}
                      </p>
                    </div>
                    <h4 className="font-semibold text-lg mb-2">{card.front}</h4>
                    <p className="text-sm opacity-80 max-h-24 overflow-hidden">{card.back}</p>
                    {card.mnemonic && (
                      <p className="mt-2 text-xs text-cyan-300">Mnemotecnia: {card.mnemonic}</p>
                    )}
                    {card.references && (
                      <p className="mt-1 text-xs opacity-60">Fuente: {card.references}</p>
                    )}
                    <div className="mt-3 flex justify-end gap-2 text-xs">
                      <button
                        onClick={() => beginEditCard(card)}
                        className="px-3 py-1 rounded-full border border-cyan-400 text-cyan-200 hover:bg-cyan-400/20"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        className="px-3 py-1 rounded-full border border-rose-400 text-rose-200 hover:bg-rose-500/10"
                      >
                        Eliminar
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {selectedCardIds.size > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => handleBulkDelete(selectedCardIds)}
                  disabled={bulkDeleting}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-rose-500/20 text-rose-200 border border-rose-500/40 hover:bg-rose-500/30 disabled:opacity-50"
                >
                  {bulkDeleting ? 'Eliminando...' : `Eliminar ${selectedCardIds.size} seleccionadas`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
    {practiceDeck && practiceCards.length > 0 && (
      <FlashcardPractice
        deck={practiceDeck}
        cards={practiceCards}
        onClose={() => setPracticeDeck(null)}
        darkMode={darkMode}
      />
    )}
    {loadingPractice && (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 text-white">
        <div className="px-6 py-4 rounded-2xl bg-slate-900 border border-slate-700 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" /> Preparando sesión...
        </div>
      </div>
    )}
    {deckModal.open && deckModal.data && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className={`w-full max-w-xl rounded-3xl ${darkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} p-6 space-y-4`}>
          <h3 className="text-2xl font-bold flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-cyan-400" /> Editar colección
          </h3>
          <div className="grid gap-3">
            <input
              type="text"
              value={deckModal.data.name}
              onChange={(e) => updateDeckModalField('name', e.target.value)}
              className={inputClasses}
              placeholder="Nombre"
            />
            <div className="grid md:grid-cols-2 gap-3">
              <input
                type="text"
                value={deckModal.data.area}
                onChange={(e) => updateDeckModalField('area', e.target.value)}
                className={inputClasses}
                placeholder="Área"
              />
              <input
                type="text"
                value={deckModal.data.topic}
                onChange={(e) => updateDeckModalField('topic', e.target.value)}
                className={inputClasses}
                placeholder="Tema"
              />
              <input
                type="text"
                value={deckModal.data.subtopic}
                onChange={(e) => updateDeckModalField('subtopic', e.target.value)}
                className={inputClasses}
                placeholder="Subtema"
              />
              <select
                value={deckModal.data.visibility}
                onChange={(e) => updateDeckModalField('visibility', e.target.value)}
                className={inputClasses}
              >
                <option value="private">Privado</option>
                <option value="public">Público</option>
              </select>
            </div>
            <textarea
              rows={2}
              value={deckModal.data.intent}
              onChange={(e) => updateDeckModalField('intent', e.target.value)}
              className={textareaClasses}
              placeholder="Objetivo / nota"
            />
            <textarea
              rows={3}
              value={deckModal.data.description}
              onChange={(e) => updateDeckModalField('description', e.target.value)}
              className={textareaClasses}
              placeholder="Descripción"
            />
            <input
              type="text"
              value={deckModal.data.tagsInput}
              onChange={(e) => updateDeckModalField('tagsInput', e.target.value)}
              className={inputClasses}
              placeholder="Tags (separados por coma)"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={closeDeckEditor}
              className="px-4 py-2 rounded-full border border-slate-600 hover:bg-slate-700"
              disabled={deckModal.saving}
            >
              Cancelar
            </button>
            <button
              onClick={handleDeckUpdate}
              disabled={deckModal.saving}
              className="px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-white font-semibold hover:shadow-lg disabled:opacity-60"
            >
              {deckModal.saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default FlashcardStudio;

